// Path: src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [available_credits, setAvailableCredits] = useState(null);

  // Load user data from localStorage on initial render
  useEffect(() => {
    const loadUserFromStorage = async () => {
      setLoading(true);
      try {
        // Try to get user data from localStorage
        const storedUser = localStorage.getItem('user');
        const token = localStorage.getItem('access_token');
        
        if (storedUser && token) {
          // Set the user from localStorage
          const parsedUser = JSON.parse(storedUser);
          setUser(parsedUser);
          
          // Optionally verify token with backend
        }
      } catch (err) {
        console.error('Error loading user:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUserFromStorage();
  }, []);

  // Login function
  const login = async (credentials) => {
    setLoading(true);
    try {
      console.log('AuthContext login credentials:', credentials);
      const response = await api.post('/auth/login/', credentials);
      console.log('AuthContext login response:', response);
      if(response.status === 401){
        return { success: false, error: 'Invalid credentials' };
      } 

      if(response.status === 400){
        return { success: false, error: 'Invalid credentials' };
      } 

      if(response.status === 500){
        return { success: false, error: 'Internal server error' };
      } 

      // Handle new API response structure
      const responseData = response.data;
      let access_token, user;
      
      if (responseData.success && responseData.data) {
        // New structure: { success: true, data: { access_token, user }, message }
        access_token = responseData.data.access_token;
        user = responseData.data.user;
      } else {
        // Fallback to old structure: { access_token, user }
        access_token = responseData.access_token;
        user = responseData.user;
      }
      
      console.log('AuthContext login response:', responseData);

      // Store token and user data in localStorage
      localStorage.setItem('access_token', access_token);
      localStorage.setItem('user', JSON.stringify(user));

      // Update state
      setUser(user);
      setError(null);
      // Return success
      return { success: true, user, access_token };
    } catch (err) {
      console.log('AuthContext login error:', err.response);
      let errorMsg = 'Invalid credentials';
      
      if (err.response?.data) {
        // Handle different response formats
        if (typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data.message) {
          errorMsg = err.response.data.message;
        } else if (err.response.data.detail) {
          errorMsg = err.response.data.detail;
        } else if (err.response.data.error) {
          errorMsg = err.response.data.error;
        }
      }
      
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  // Signup function
  const signup = async (userData) => {
    setLoading(true);
    try {
      const response = await api.post('/auth/signup/', userData);
      setError(null);
      return {
        success: true,
        data: response.data
      };
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed');
      return {
        success: false,
        data: err.response?.data || {}
      };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await api.post('/auth/logout/');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Always clear local storage and state
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Oauth Login function
  const oauthLogin = async (userData, accessToken) => {
    try {
      localStorage.setItem("access_token", accessToken);
      localStorage.setItem("user", JSON.stringify(userData));
      setUser(userData);
    } catch (err) {
      console.log('Login failed');
    }
  };


  // Value object to provide through context
  const value = {
    user,
    setUser,
    loading,
    error,
    login,
    logout,
    isAuthenticated,
    available_credits,
    oauthLogin,
    signup
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};